import { colors } from "@zfp-akademie-a-s/zfp-design";
import { formatCurrencyShortText } from "Infrastructure/Utils/CurrencyUtils";
import { generateCompoundInterestData } from "./CommonChartUtils";

type GenerateMonthlyWithdrawChartDataParams = {
  readonly monthlyContributionAmount: number;
  readonly accumulationPhaseDuration: number;
  readonly drawdownPhaseDuration: number;
  readonly drawAmount: number;
  readonly accumulationInterestRate: number;
  readonly drawdownInterestRate: number;
  readonly inflationRate: number;
};

type MonthlyWithdrawChartData = {
  readonly accumulationPhaseData: number[];
  readonly drawdownPhaseData: number[];
  readonly actualDurationInMonths: number;
  readonly milestoneDifferenceRatio: number;
};

type GenerateDrawdownDataParams = {
  readonly startAmount?: number;
  readonly startIndex?: number;
  readonly drawdownPhaseDuration: number;
  readonly drawAmount: number;
  readonly interestRate?: number;
  readonly inflationRate?: number;
};

export const generateMonthlyWithdrawChartData = ({
  monthlyContributionAmount,
  accumulationPhaseDuration,
  drawdownPhaseDuration,
  accumulationInterestRate,
  drawdownInterestRate,
  inflationRate,
  drawAmount,
}: GenerateMonthlyWithdrawChartDataParams): MonthlyWithdrawChartData => {
  const accumulationPhaseData = generateCompoundInterestData({
    monthlyContributionAmount,
    inflationRate,
    interestRate: accumulationInterestRate,
    durationInMonths: accumulationPhaseDuration,
  });

  const accumulatedAmount = accumulationPhaseData[accumulationPhaseData.length - 1];
  const drawdownPhaseData = accumulationPhaseData.concat(
    _generateDrawdownPhaseData({
      startAmount: accumulatedAmount,
      drawdownPhaseDuration,
      drawAmount,
      interestRate: drawdownInterestRate,
      inflationRate: inflationRate,
    }),
  );

  const milestoneDifferenceRatio = Math.abs(accumulationPhaseData.length - drawdownPhaseData.length);
  const actualDurationInMonths = drawdownPhaseData.length;

  return {
    accumulationPhaseData,
    drawdownPhaseData,
    milestoneDifferenceRatio,
    actualDurationInMonths,
  };
};

export const calculateTargetAmount = (
  drawAmount: number,
  durationInMonths: number,
  drawdownInterestRate: number,
  inflationRate = 0,
): number => {
  const compoundRate = (drawdownInterestRate - inflationRate) / 100 / 12;
  const formula = (drawAmount * (1 - (1 + compoundRate) ** -durationInMonths)) / compoundRate;

  return Number(formula.toFixed());
};

const _generateDrawdownPhaseData = ({
  startAmount = 0,
  startIndex = 0,
  drawdownPhaseDuration,
  drawAmount,
  inflationRate = 0,
  interestRate = 0,
}: GenerateDrawdownDataParams): number[] => {
  const monthlyCompoundRate = (interestRate - inflationRate) / 100 / 12;
  const result = new Array(startIndex).fill(0);

  if (monthlyCompoundRate === 0) {
    // return result.concat(new Array(drawdownPhaseDuration).fill(drawAmount));
    let currentAmount = startAmount;
    for (let i = 0; i <= drawdownPhaseDuration; i++) {
      if (i === 0) continue; // do not push first value
      currentAmount = currentAmount - drawAmount;
      result.push(currentAmount > 0 ? currentAmount : 0);
    }
    return result;
  }

  for (let i = 0; i <= drawdownPhaseDuration; i++) {
    if (i === 0) continue; // do not push first value
    const monthlyValue =
      startAmount * (1 + monthlyCompoundRate) ** i - drawAmount * (((1 + monthlyCompoundRate) ** i - 1) / monthlyCompoundRate);
    if (monthlyValue < 0) {
      result.push(0);
    } else {
      result.push(monthlyValue);
    }
  }

  return result;
};

export const getMonhtlyWithdrawChartAnnotations = (targetAmount: number, accumulationPhaseDuration: number) => {
  const commonLineProps = {
    type: "line" as const,
    borderColor: "rgba(0, 0, 0, 0.05)",
    borderWidth: 1,
    borderDash: [8, 4],
  };

  const commonLabelProps = {
    display: true,
    backgroundColor: "transparent",
    color: colors.textSecondary,
    position: "start" as const,
    yAdjust: 10,
    font: {
      weight: 400,
      size: 10,
    },
  };

  return {
    reserveCreated: {
      yMin: 0,
      yMax: targetAmount,
      xMin: accumulationPhaseDuration - 1,
      xMax: accumulationPhaseDuration - 1,
      borderColor: "#000",
      borderWidth: 1,
      borderDash: [3, 5],
    },
    reserveCreatedDot: {
      type: "point" as const,
      xValue: accumulationPhaseDuration - 1,
      yValue: 0,
      backgroundColor: "#000",
      borderWidth: 0,
      radius: 4,
    },
    targetAmount: {
      label: {
        ...commonLabelProps,
        content: formatCurrencyShortText(targetAmount),
      },
      ...commonLineProps,
      yMax: targetAmount,
      yMin: targetAmount,
    },
  };
};
